import React, { useEffect, useState } from 'react';
import LeftArrowDI from '@atlaskit/icon/glyph/arrow-left-circle';
import BacklogIcon from '@atlaskit/icon/core/migration/backlog';
import BoardIconDI from '@atlaskit/icon/glyph/board';
import ChartIcon from '@atlaskit/icon/core/migration/chart-trend--graph-line';
import {
	NavigationHeader as NavigationHeaderDI,
	NestableNavigationContent as NestableNavigationContentDI,
	Section as SectionDI,
	SideNavigation as SideNavigationDI,
	SkeletonItem as SkeletonItemDI,
} from '@atlaskit/side-navigation';
import { SCRUM_BOARD } from '@atlassian/jira-common-constants/src/board-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl as useIntlDI } from '@atlassian/jira-intl';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useBoard as useBoardDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { ContextualAnalyticsData as ContextualAnalyticsDataDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { NestingMenuItem as NestingMenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { ProjectHeader as ProjectHeaderDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/project-header/main.tsx';
import { ScopeSwitcher as ScopeSwitcherDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/scope-switcher/index.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlPathname as matchUrlPathnameDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { useCurrentRoute as useCurrentRouteDI } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import {
	usePathParam as usePathParamDI,
	useQueryParam as useQueryParamDI,
} from '@atlassian/react-resource-router';
import { MENU_ID } from '../../common/constants.tsx';
import { useAvatar as useAvatarDI } from '../../controllers/avatar/index.tsx';
import { useFeatures as useFeaturesDI } from '../../controllers/features/index.tsx';
import messages from './messages.tsx';
import { Reports as ReportsDI } from './reports/main.tsx';
import type { MenuProps } from './types.tsx';
import { backlogSelected, boardSelected, calculateStack } from './util/index.tsx';

const useThrowAvatarDI = withThrowOnError(useAvatarDI);
const useThrowFeaturesDI = withThrowOnError(useFeaturesDI);
const useThrowBoardDI = withThrowOnError(useBoardDI);

export const Menu = ({
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	BoardIcon = BoardIconDI,
	ContextualAnalyticsData = ContextualAnalyticsDataDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	LeftArrow = LeftArrowDI,
	MenuItem = MenuItemDI,
	NavigationHeader = NavigationHeaderDI,
	NestableNavigationContent = NestableNavigationContentDI,
	// @ts-expect-error - TS2339 - Property 'NestingMenuItem' does not exist on type 'MenuProps'.
	NestingMenuItem = NestingMenuItemDI,
	ProjectHeader = ProjectHeaderDI,
	Reports = ReportsDI,
	ScopeSwitcher = ScopeSwitcherDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	SideNavigation = SideNavigationDI,
	useAvatar = useThrowAvatarDI,
	useBoard = useThrowBoardDI,
	// @ts-expect-error - TS2339 - Property 'useCurrentRoute' does not exist on type 'MenuProps'.
	useCurrentRoute = useCurrentRouteDI,
	useFeatures = useThrowFeaturesDI,
	useIntl = useIntlDI,
	// @ts-expect-error - TS2339 - Property 'usePathParam' does not exist on type 'MenuProps'.
	usePathParam = usePathParamDI,
	// @ts-expect-error - TS2339 - Property 'useQueryParam' does not exist on type 'MenuProps'.
	useQueryParam = useQueryParamDI,
	matchUrlPathname = matchUrlPathnameDI,
}: MenuProps) => {
	const { formatMessage } = useIntl();
	const route = useCurrentRoute();
	const [paramAccountId] = usePathParam('accountId');
	const [isLocationlessBoard] = useQueryParam('locationless');
	const { data: avatar } = useAvatar();
	const { data: features } = useFeatures();
	const { data: board } = useBoard();

	const [stack, setStack] = useState<string[]>(calculateStack(route));

	useEffect(() => {
		setStack(calculateStack(route));
	}, [route]);

	if (avatar == null || board == null || features == null) return <SkeletonItem />;

	const shouldUsePrettyUrl = !isLocationlessBoard;

	const boardAccountId = paramAccountId ?? '';
	const BackMenuItem = {
		href: shouldUsePrettyUrl
			? `/jira/people/${boardAccountId}/boards/${board.id}`
			: `/secure/RapidBoard.jspa?rapidView=${board.id}&locationless=true`,
		iconBefore: <LeftArrow label="" />,
		// @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
		onClick: (event) => event.preventDefault(),
		title: formatMessage(messages.itemBack),
	};

	// Adding a small function to handle the rendering of the reports menu item because for UIF
	// we need an actual anchor tag to completely refresh the page due to some issues around
	// the company managed report pages (which are part of the legacy FE).
	const renderReportsLink = () => {
		// Early termination. No reports feature means no link.
		if (!shouldRenderNesting(features.hasReports, stack, 'reports')) {
			return null;
		}

		const oldReportsHref = shouldUsePrettyUrl
			? `/jira/people/${boardAccountId}/boards/${board.id}/reports/burndown-chart`
			: `/secure/RapidBoard.jspa?rapidView=${board.id}&view=reporting&locationless=true`;

		const reportsHref = `/jira/people/${boardAccountId}/boards/${board.id}/reports`;

		return (
			<NestingMenuItem
				reloadOnNavigationToNesting
				analytics={{ itemId: 'reports' }}
				href={fg('reports-overview-page-migration') ? reportsHref : oldReportsHref}
				iconBefore={<ChartIcon spacing="spacious" label="" />}
				id="reports"
				overrides={{ BackMenuItem }}
				title={formatMessage(messages.itemReports)}
				selectedOn={fg('reports-overview-page-migration') ? matchUrlPathname() : false}
			>
				<Reports enableKeyboardShortcuts />
			</NestingMenuItem>
		);
	};

	return (
		<ContextualAnalyticsData menuId={MENU_ID} stack={stack}>
			<SideNavigation label="">
				<NavigationHeader>
					<ProjectHeader
						description={formatMessage(messages.headerDescription)}
						avatarUrl={avatar.url ?? ''}
						name={avatar.name ?? ''}
					/>
				</NavigationHeader>
				<NestableNavigationContent stack={stack} onChange={setStack}>
					<ScopeSwitcher />
					<Section>
						{features.hasBacklog && (
							<MenuItem
								analytics={{ itemId: `backlog-${board.type}` }}
								href={
									shouldUsePrettyUrl
										? `/jira/people/${boardAccountId}/boards/${board.id}/backlog`
										: `/secure/RapidBoard.jspa?rapidView=${board.id}&view=planning&locationless=true`
								}
								iconBefore={<BacklogIcon spacing="spacious" label="" />}
								selectedOn={shouldUsePrettyUrl ? matchUrlPathname() : backlogSelected}
							>
								{formatMessage(messages.backlog)}
							</MenuItem>
						)}
						{features.hasBoard && (
							<MenuItem
								analytics={{ itemId: `board-${board.type}` }}
								href={
									shouldUsePrettyUrl
										? `/jira/people/${boardAccountId}/boards/${board.id}`
										: `/secure/RapidBoard.jspa?rapidView=${board.id}&locationless=true`
								}
								iconBefore={<BoardIcon label="" />}
								selectedOn={shouldUsePrettyUrl ? matchUrlPathname() : boardSelected}
							>
								{formatMessage(
									board.type === SCRUM_BOARD ? messages.scrumBoard : messages.kanbanBoard,
								)}
							</MenuItem>
						)}
						{renderReportsLink()}
					</Section>
				</NestableNavigationContent>
			</SideNavigation>
		</ContextualAnalyticsData>
	);
};
